import React, { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';
import { Carousel } from 'react-responsive-carousel';
import 'react-responsive-carousel/lib/styles/carousel.min.css';
import ReCAPTCHA from 'react-google-recaptcha';
import Header from '../components/Header';
import Footer from '../components/Footer';
import PreApprovalWidget from '../components/PreApprovalWidget';
import { useToast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

const HomePage = () => {
  const { toast } = useToast();
  const location = useLocation();
  const recaptchaRef = useRef(null);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    inquiry_notes: '',
    subscribe_to_mailing_list: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const [showProvinceMap, setShowProvinceMap] = useState(false);

  // Preload first 2 carousel images for better LCP
  useEffect(() => {
    const preloadImages = [
      'https://images.unsplash.com/photo-1766595680974-e63877a2ab5b?w=1920&q=80&fm=webp',
      'https://images.unsplash.com/photo-1629909613654-28e377c37b09?w=1920&q=80&fm=webp'
    ];
    
    preloadImages.forEach((src, index) => {
      const link = document.createElement('link');
      link.rel = index === 0 ? 'preload' : 'prefetch';
      link.as = 'image';
      link.href = src;
      link.fetchPriority = index === 0 ? 'high' : 'auto';
      document.head.appendChild(link);
    });
  }, []);

  // Scroll to "Ready to Apply" section when visiting /applynow or with #ready-to-apply hash
  useEffect(() => {
    const shouldScrollToApply =
      location.pathname === '/applynow' || location.hash === '#ready-to-apply';
    if (!shouldScrollToApply) return;
    const timer = setTimeout(() => {
      const el = document.getElementById('ready-to-apply');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 250);
    return () => clearTimeout(timer);
  }, [location.pathname, location.hash]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleRecaptchaChange = (token) => {
    setRecaptchaToken(token);
  };

  // Google Ads conversion tracking helper
  const trackConversion = (eventName) => {
    if (typeof window.gtag === 'function') {
      // Google Ads conversion tracking with labels and values
      if (eventName === 'business_use_application') {
        window.gtag('event', 'conversion', {
          'send_to': 'AW-11080823629/ihQUCJ6gr-MbEM3m36Mp',
          'value': 400.0,
          'currency': 'CAD'
        });
      } else if (eventName === 'personal_use_application') {
        window.gtag('event', 'conversion', {
          'send_to': 'AW-11080823629/wWXOCKGgr-MbEM3m36Mp',
          'value': 100.0,
          'currency': 'CAD'
        });
      } else if (eventName === 'contact_form_submission') {
        window.gtag('event', 'conversion', {
          'send_to': 'AW-11080823629/-m0aCKSgr-MbEM3m36Mp',
          'value': 100.0,
          'currency': 'CAD'
        });
      }
      // Also send to GA4 for analytics
      window.gtag('event', eventName);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check reCAPTCHA
    if (!recaptchaToken) {
      toast({
        title: "Verification Required",
        description: "Please complete the reCAPTCHA verification.",
        variant: "destructive"
      });
      return;
    }
    
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, {
        ...formData,
        recaptcha_token: recaptchaToken
      });
      
      // Track contact form conversion
      trackConversion('contact_form_submission');
      
      setFormSubmitted(true);
      setFormData({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        inquiry_notes: '',
        subscribe_to_mailing_list: false
      });
      setRecaptchaToken(null);
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to submit form. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleProvinceClick = (province) => {
    const eastProvinces = ['ontario', 'quebec', 'new-brunswick', 'nova-scotia', 'pei', 'newfoundland', 'nunavut', 'northwest-territories', 'yukon'];
    const westProvinces = ['british-columbia', 'alberta', 'saskatchewan', 'manitoba'];
    
    // Track Business Use application click
    trackConversion('business_use_application');
    
    if (eastProvinces.includes(province)) {
      window.open('https://app.useanvil.com/weld/equipease-lease-co/creditapplicationAFS', '_blank');
    } else if (westProvinces.includes(province)) {
      window.open('https://app.useanvil.com/weld/equipease-lease-co/credit-application-form-heavy-equipment', '_blank');
    }
    setShowProvinceMap(false);
  };

  const handlePersonalUseClick = () => {
    // Track Personal Use application click
    trackConversion('personal_use_application');
    window.open('https://share.hsforms.com/1ebxMZSTqT8uHvOcu3XLh5Qbne2o', '_blank');
  };

  // Carousel slides with optimized image URLs (WebP format, proper dimensions) - refreshed 2026-02
  const carouselSlides = [
    {
      image: 'https://images.unsplash.com/photo-1766595680974-e63877a2ab5b?w=1920&q=80&fm=webp',
      imageFallback: 'https://images.unsplash.com/photo-1766595680974-e63877a2ab5b?w=1920&q=80',
      title: 'Construction & Heavy Equipment',
      subtitle: 'Financing solutions for builders and contractors',
      alt: 'Yellow excavator working on a construction site - EquipEASE construction equipment financing in Canada',
      width: 1920,
      height: 600
    },
    {
      image: 'https://images.unsplash.com/photo-1629909613654-28e377c37b09?w=1920&q=80&fm=webp',
      imageFallback: 'https://images.unsplash.com/photo-1629909613654-28e377c37b09?w=1920&q=80',
      title: 'Medical & Dental Equipment',
      subtitle: 'Advanced technology for healthcare professionals',
      alt: 'Modern dental clinic operatory with advanced patient chair - EquipEASE healthcare equipment leasing',
      width: 1920,
      height: 600
    },
    {
      image: 'https://images.unsplash.com/photo-1592805144716-feeccccef5ac?w=1920&q=80&fm=webp',
      imageFallback: 'https://images.unsplash.com/photo-1592805144716-feeccccef5ac?w=1920&q=80',
      title: 'Transportation & Trucking',
      subtitle: 'Helping owner operators achieve their dreams',
      alt: 'Classic American conventional semi truck on highway at sunset - EquipEASE transportation financing for owner operators',
      width: 1920,
      height: 600
    },
    {
      image: 'https://images.unsplash.com/photo-1719932124314-8b6acaf60104?w=1920&q=80&fm=webp',
      imageFallback: 'https://images.unsplash.com/photo-1719932124314-8b6acaf60104?w=1920&q=80',
      title: 'Agriculture Industry',
      subtitle: 'Modern farming equipment for growing operations',
      alt: 'Green farm tractor in a Canadian field - EquipEASE agriculture equipment financing',
      width: 1920,
      height: 600
    },
    {
      image: 'https://images.unsplash.com/photo-1738669468852-1a30a9e3d7a2?w=1920&q=80&fm=webp',
      imageFallback: 'https://images.unsplash.com/photo-1738669468852-1a30a9e3d7a2?w=1920&q=80',
      title: 'Personal Use - RV, Powersport & Marine',
      subtitle: 'Finance your adventure and recreational dreams',
      alt: 'Recreational four-wheeler ATV parked in a Canadian mountain field - EquipEASE powersport vehicle financing',
      width: 1920,
      height: 600
    }
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <Helmet>
        <title>EquipEASE Lease Co. - Equipment Leasing & Financing for Canadian Businesses</title>
        <meta name="description" content="EquipEASE provides flexible equipment leasing & financing for Canadian businesses. Construction, medical, agricultural equipment and more. Call 1-844-250-EASE for competitive rates and quick approvals." />
        <link rel="canonical" href="https://equipease.ca/" />
      </Helmet>
      
      <Header />
      
      {/* Hero Carousel Section */}
      <section className="relative">
        <Carousel
          autoPlay
          infiniteLoop
          interval={5000}
          showThumbs={false}
          showStatus={false}
          showArrows={true}
          className="hero-carousel"
          stopOnHover={true}
          swipeable={true}
          emulateTouch={true}
          preventMovementUntilSwipeScrollTolerance={true}
          swipeScrollTolerance={50}
        >
          {carouselSlides.map((slide, index) => (
            <div key={index} className="hero-slide">
              <picture>
                <source 
                  srcSet={slide.image} 
                  type="image/webp"
                />
                <img
                  src={slide.imageFallback || slide.image}
                  alt={slide.alt}
                  width={slide.width}
                  height={slide.height}
                  loading={index === 0 ? 'eager' : 'lazy'}
                  fetchpriority={index === 0 ? 'high' : 'auto'}
                  decoding={index === 0 ? 'sync' : 'async'}
                  className="hero-slide-image"
                />
              </picture>
              <div className="absolute inset-0 bg-gradient-to-r from-[#333333]/90 via-[#333333]/70 to-transparent"></div>
              <div className="absolute inset-0 flex items-center">
                <div className="container mx-auto px-4">
                  <div className="max-w-3xl text-left text-white">
                    <h1 className="text-4xl md:text-6xl font-bold mb-4 drop-shadow-lg">
                      {slide.title}
                    </h1>
                    <p className="text-xl md:text-2xl mb-8 text-gray-100 drop-shadow-md">
                      {slide.subtitle}
                    </p>
                    <a 
                      href="#contact" 
                      className="inline-block bg-[#FF6600] hover:bg-[#FF9933] text-white px-8 py-4 rounded-lg font-semibold text-lg transition-colors shadow-lg"
                    >
                      Get Free Consultation
                    </a>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </Carousel>
        <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-white to-transparent z-10"></div>
      </section>

      {/* Why Choose Section */}
      <section id="why-choose" className="relative py-20 bg-gray-50 overflow-hidden">
        <div className="ee-flow-bg" />
        <div className="container mx-auto px-4 relative z-10">
          <h2 className="text-4xl font-bold text-center mb-3 text-[#333]">Why Choose EquipEASE?</h2>
          <p className="text-center text-[#666] mb-12 max-w-xl mx-auto">10 reasons Canadian businesses trust us with their equipment financing.</p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-5 max-w-7xl mx-auto">
            {[
              {
                title: 'You Find It, We Finance It',
                desc: 'Found the equipment? We handle the rest.',
                icon: 'M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z',
                accent: 'from-orange-500 to-amber-500',
              },
              {
                title: 'Flexible Financing',
                desc: 'Tailored solutions that fit your budget.',
                icon: 'M10.5 6h9.75M10.5 6a1.5 1.5 0 11-3 0m3 0a1.5 1.5 0 10-3 0M3.75 6H7.5m3 12h9.75m-9.75 0a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m-3.75 0H7.5m9-6h3.75m-3.75 0a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m-9.75 0h9.75',
                accent: 'from-blue-500 to-cyan-500',
              },
              {
                title: 'Easy Application',
                desc: 'Apply in minutes, not hours.',
                icon: 'M15.59 14.37a6 6 0 01-5.84 7.38v-4.8m5.84-2.58a14.98 14.98 0 006.16-12.12A14.98 14.98 0 009.631 8.41m5.96 5.96a14.926 14.926 0 01-5.841 2.58m-.119-8.54a6 6 0 00-7.381 5.84h4.8m2.58-5.84a14.927 14.927 0 00-2.58 5.84m2.699 2.7c-.103.021-.207.041-.311.06a15.09 15.09 0 01-2.448-2.448 14.9 14.9 0 01.06-.312m-2.24 2.39a4.493 4.493 0 00-1.757 4.306 4.493 4.493 0 004.306-1.758M16.5 9a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z',
                accent: 'from-emerald-500 to-green-500',
              },
              {
                title: 'Quick Approvals',
                desc: 'Competitive rates, fast turnaround.',
                icon: 'M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z',
                accent: 'from-yellow-500 to-orange-500',
              },
              {
                title: 'Total Transparency',
                desc: 'No hidden fees. Ever.',
                icon: 'M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178zM15 12a3 3 0 11-6 0 3 3 0 016 0z',
                accent: 'from-violet-500 to-purple-500',
              },
              {
                title: 'Expert Brokers',
                desc: 'We negotiate the best lender rates for you.',
                icon: 'M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z',
                accent: 'from-pink-500 to-rose-500',
              },
              {
                title: 'Industry Expertise',
                desc: 'Years of experience across all sectors.',
                icon: 'M4.26 10.147a60.436 60.436 0 00-.491 6.347A48.627 48.627 0 0112 20.904a48.627 48.627 0 018.232-4.41 60.46 60.46 0 00-.491-6.347m-15.482 0a50.57 50.57 0 00-2.658-.813A59.905 59.905 0 0112 3.493a59.902 59.902 0 0110.399 5.84c-.896.248-1.783.52-2.658.814m-15.482 0A50.697 50.697 0 0112 13.489a50.702 50.702 0 017.74-3.342M6.75 15a.75.75 0 100-1.5.75.75 0 000 1.5zm0 0v-3.675A55.378 55.378 0 0112 8.443m-7.007 11.55A5.981 5.981 0 006.75 15.75v-1.5',
                accent: 'from-teal-500 to-cyan-500',
              },
              {
                title: 'Dedicated Support',
                desc: 'A real person with you every step.',
                icon: 'M20.25 8.511c.884.284 1.5 1.128 1.5 2.097v4.286c0 1.136-.847 2.1-1.98 2.193-.34.027-.68.052-1.02.072v3.091l-3-3c-1.354 0-2.694-.055-4.02-.163a2.115 2.115 0 01-.825-.242m9.345-8.334a2.126 2.126 0 00-.476-.095 48.64 48.64 0 00-8.048 0c-1.131.094-1.976 1.057-1.976 2.192v4.286c0 .837.46 1.58 1.155 1.951m9.345-8.334V6.637c0-1.621-1.152-3.026-2.76-3.235A48.455 48.455 0 0011.25 3c-2.115 0-4.198.137-6.24.402-1.608.209-2.76 1.614-2.76 3.235v6.226c0 1.621 1.152 3.026 2.76 3.235.577.075 1.157.14 1.74.194V21l4.155-4.155',
                accent: 'from-indigo-500 to-blue-500',
              },
              {
                title: 'Timely Funding',
                desc: 'Get your equipment faster.',
                icon: 'M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z',
                accent: 'from-red-500 to-orange-500',
              },
              {
                title: 'Focus on Growth',
                desc: 'We handle financing. You build your empire.',
                icon: 'M2.25 18L9 11.25l4.306 4.307a11.95 11.95 0 015.814-5.519l2.74-1.22m0 0l-5.94-2.28m5.94 2.28l-2.28 5.941',
                accent: 'from-lime-500 to-green-500',
              },
            ].map((item, index) => (
              <div
                key={index}
                className="group relative bg-white rounded-2xl p-6 shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border border-gray-100 overflow-hidden"
              >
                {/* Hover gradient accent */}
                <div className={`absolute inset-0 bg-gradient-to-br ${item.accent} opacity-0 group-hover:opacity-100 transition-opacity duration-300`} />
                <div className="absolute inset-[2px] bg-white rounded-2xl group-hover:bg-white transition-colors" />

                <div className="relative z-10">
                  <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${item.accent} flex items-center justify-center mb-4 shadow-sm group-hover:scale-110 transition-transform duration-300`}>
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d={item.icon} />
                    </svg>
                  </div>
                  <h3 className="text-[15px] font-bold text-[#333] mb-1.5 leading-snug">{item.title}</h3>
                  <p className="text-[13px] text-[#666] leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Stats Section - Freshness signals */}
      <section className="relative py-14 bg-gradient-to-br from-[#1a1a2e] to-[#16213e] text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10 pointer-events-none">
          <div className="absolute top-0 right-0 w-96 h-96 bg-[#FF6600] rounded-full blur-[150px]" />
          <div className="absolute bottom-0 left-0 w-72 h-72 bg-[#FF9933] rounded-full blur-[120px]" />
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center mb-10">
            <span className="inline-block bg-[#FF6600]/20 text-[#FF9933] text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full mb-3">
              Trusted Across Canada
            </span>
            <h2 className="text-3xl md:text-4xl font-extrabold">Real Numbers. Real Results.</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-5xl mx-auto">
            {[
              { value: '$50M+', label: 'Equipment Financed' },
              { value: '500+', label: 'Canadian Businesses' },
              { value: '48hr', label: 'Average Approval' },
              { value: '95%*', label: 'Approval Rate' },
            ].map((stat) => (
              <div
                key={stat.label}
                className="text-center p-6 rounded-2xl bg-white/5 backdrop-blur-sm border border-white/10 hover:border-[#FF6600]/40 hover:-translate-y-1 transition-all"
                data-testid={`trust-stat-${stat.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className="text-4xl md:text-5xl font-extrabold text-[#FF6600] mb-2">{stat.value}</div>
                <div className="text-sm md:text-base text-gray-300 font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
          <p className="text-center text-xs text-gray-400 mt-6 max-w-3xl mx-auto italic">
            *Approval rate reflects EquipEASE-brokered submissions to our lender network. Individual approvals depend on lender criteria, applicant credit profile, business tenure, and documentation. Rates and terms vary.
          </p>
        </div>
      </section>

      {/* Pre-Approval Widget Section */}
      <section id="pre-approval" className="relative py-20 bg-white overflow-hidden">
        <div className="ee-flow-bg" />
        <div className="container mx-auto px-4 relative z-10">
          <h2 className="text-4xl font-bold text-center mb-4 text-[#333333]">Get Pre-Approved in Minutes</h2>
          <p className="text-center text-[#4A4A4A] mb-8 max-w-xl mx-auto">
            Answer a few quick questions and a financing specialist will contact you within 2 business hours with your estimated rates.
          </p>
          <PreApprovalWidget />
        </div>
      </section>

      {/* Ready to Apply Section */}
      <section id="ready-to-apply" className="relative py-20 bg-gray-50 overflow-hidden">
        <div className="ee-flow-bg" />
        <div className="container mx-auto px-4 relative z-10">
          <h2 className="text-4xl font-bold text-center mb-3 text-[#333]">Ready to Apply Now?</h2>
          <p className="text-center text-[#666] mb-12 max-w-lg mx-auto">Choose your path — we've got financing options for both.</p>
          
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* Business Use Card */}
            <div 
              onClick={() => setShowProvinceMap(true)}
              data-testid="business-use-card"
              className="group relative cursor-pointer rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-1"
            >
              {/* Background */}
              <div className="absolute inset-0 bg-gradient-to-br from-[#FF6600] via-[#FF7722] to-[#FF9933] transition-all duration-500" />
              {/* Decorative circles */}
              <div className="absolute -top-10 -right-10 w-40 h-40 bg-white/10 rounded-full group-hover:scale-125 transition-transform duration-500" />
              <div className="absolute -bottom-12 -left-12 w-48 h-48 bg-white/5 rounded-full group-hover:scale-110 transition-transform duration-700" />

              <div className="relative z-10 p-10 md:p-12">
                <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center mb-5 group-hover:scale-110 group-hover:rotate-3 transition-all duration-300">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 21h19.5m-18-18v18m10.5-18v18m6-13.5V21M6.75 6.75h.75m-.75 3h.75m-.75 3h.75m3-6h.75m-.75 3h.75m-.75 3h.75M6.75 21v-3.375c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21M3 3h12m-.75 4.5H21m-3.75 3h.008v.008h-.008v-.008zm0 3h.008v.008h-.008v-.008zm0 3h.008v.008h-.008v-.008z" />
                  </svg>
                </div>
                <h3 className="text-2xl md:text-3xl font-extrabold text-white mb-3">Business Use</h3>
                <ul className="space-y-2 mb-6">
                  <li className="flex items-center text-white/90 text-sm"><svg className="w-4 h-4 mr-2 text-white/70 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>Construction, trucking, medical & more</li>
                  <li className="flex items-center text-white/90 text-sm"><svg className="w-4 h-4 mr-2 text-white/70 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>Financing from $5K to $500K+</li>
                  <li className="flex items-center text-white/90 text-sm"><svg className="w-4 h-4 mr-2 text-white/70 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>Tax-deductible lease payments</li>
                </ul>
                <span className="inline-flex items-center text-white font-bold text-sm group-hover:gap-3 gap-2 transition-all">
                  Start Application
                  <svg className="w-5 h-5 transition-transform group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" /></svg>
                </span>
              </div>
            </div>

            {/* Personal Use Card */}
            <div 
              onClick={handlePersonalUseClick}
              data-testid="personal-use-card"
              className="group relative cursor-pointer rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-1"
            >
              {/* Background */}
              <div className="absolute inset-0 bg-gradient-to-br from-[#1a1a2e] via-[#2d2d44] to-[#3a3a55] transition-all duration-500" />
              {/* Decorative circles */}
              <div className="absolute -top-10 -right-10 w-40 h-40 bg-white/5 rounded-full group-hover:scale-125 transition-transform duration-500" />
              <div className="absolute -bottom-12 -left-12 w-48 h-48 bg-[#FF6600]/10 rounded-full group-hover:scale-110 transition-transform duration-700" />

              <div className="relative z-10 p-10 md:p-12">
                <div className="w-16 h-16 bg-white/10 backdrop-blur-sm rounded-2xl flex items-center justify-center mb-5 group-hover:scale-110 group-hover:-rotate-3 transition-all duration-300">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
                  </svg>
                </div>
                <h3 className="text-2xl md:text-3xl font-extrabold text-white mb-3">Personal Use</h3>
                <ul className="space-y-2 mb-6">
                  <li className="flex items-center text-white/80 text-sm"><svg className="w-4 h-4 mr-2 text-[#FF6600] flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>ATVs, boats, RVs, snowmobiles & more</li>
                  <li className="flex items-center text-white/80 text-sm"><svg className="w-4 h-4 mr-2 text-[#FF6600] flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>Competitive personal rates</li>
                  <li className="flex items-center text-white/80 text-sm"><svg className="w-4 h-4 mr-2 text-[#FF6600] flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>Fast approval, simple process</li>
                </ul>
                <span className="inline-flex items-center text-[#FF6600] font-bold text-sm group-hover:gap-3 gap-2 transition-all">
                  Start Application
                  <svg className="w-5 h-5 transition-transform group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" /></svg>
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Province Selection Modal */}
      {showProvinceMap && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowProvinceMap(false)}>
          <div className="bg-white rounded-lg p-8 max-w-4xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-3xl font-bold text-[#333333]">What Province Are You In?</h3>
              <button onClick={() => setShowProvinceMap(false)} className="text-gray-500 hover:text-gray-700">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {[
                { name: 'British Columbia', id: 'british-columbia' },
                { name: 'Alberta', id: 'alberta' },
                { name: 'Saskatchewan', id: 'saskatchewan' },
                { name: 'Manitoba', id: 'manitoba' },
                { name: 'Ontario', id: 'ontario' },
                { name: 'Quebec', id: 'quebec' },
                { name: 'New Brunswick', id: 'new-brunswick' },
                { name: 'Nova Scotia', id: 'nova-scotia' },
                { name: 'Prince Edward Island', id: 'pei' },
                { name: 'Newfoundland', id: 'newfoundland' },
                { name: 'Yukon', id: 'yukon' },
                { name: 'Northwest Territories', id: 'northwest-territories' },
                { name: 'Nunavut', id: 'nunavut' }
              ].map((province) => (
                <button
                  key={province.id}
                  onClick={() => handleProvinceClick(province.id)}
                  className="bg-[#FF6600] hover:bg-[#FF9933] text-white p-4 rounded-lg font-semibold transition-colors shadow-md hover:shadow-lg"
                >
                  {province.name}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Contact Form Section */}
      <section id="contact" className="relative py-20 bg-white overflow-hidden">
        <div className="ee-flow-bg" />
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-2xl mx-auto">
            <h2 className="text-4xl font-bold text-center mb-4 text-[#333333]">Have Any Questions?</h2>
            <p className="text-center text-[#4A4A4A] mb-4">
              Share your details, and we'll provide a free, personalized consultation to get you started!
            </p>
            <p className="text-center mb-8">
              <span className="text-[#4A4A4A]">Or call us at </span>
              <a href="tel:1-844-250-3273" className="text-[#FF6600] hover:text-[#FF9933] font-bold text-xl transition-colors">
                1-844-250-EASE
              </a>
            </p>
            
            {formSubmitted ? (
              <div className="bg-green-50 border border-green-200 rounded-lg p-8 text-center shadow-md">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                </div>
                <h3 className="text-2xl font-bold text-green-800 mb-2">Thank You!</h3>
                <p className="text-green-700 mb-4">
                  Your inquiry has been submitted successfully. One of our financing specialists will contact you within 24 hours.
                </p>
                <p className="text-sm text-green-600 mb-6">
                  Check your email for a confirmation message.
                </p>
                <button
                  onClick={() => setFormSubmitted(false)}
                  className="bg-[#FF6600] hover:bg-[#FF9933] text-white px-6 py-3 rounded-lg font-semibold transition-colors"
                >
                  Submit Another Inquiry
                </button>
              </div>
            ) : (
            <form onSubmit={handleSubmit} className="bg-gray-50 p-8 rounded-lg shadow-md border-t-4 border-[#FF6600]">
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-sm font-medium text-[#333333] mb-2">First Name</label>
                  <input
                    type="text"
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF6600] focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#333333] mb-2">Last Name</label>
                  <input
                    type="text"
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF6600] focus:border-transparent"
                  />
                </div>
              </div>
              
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-sm font-medium text-[#333333] mb-2">Email</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF6600] focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#333333] mb-2">Phone</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF6600] focus:border-transparent"
                  />
                </div>
              </div>
              
              <div className="mb-6">
                <label className="block text-sm font-medium text-[#333333] mb-2">Inquiry Notes</label>
                <textarea
                  name="inquiry_notes"
                  value={formData.inquiry_notes}
                  onChange={handleChange}
                  rows="4"
                  placeholder="Tell us about your equipment financing needs..."
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF6600] focus:border-transparent resize-none"
                />
              </div>
              
              <div className="mb-6">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    name="subscribe_to_mailing_list"
                    checked={formData.subscribe_to_mailing_list}
                    onChange={handleChange}
                    className="w-4 h-4 text-[#FF6600] border-gray-300 rounded focus:ring-[#FF6600]"
                  />
                  <span className="text-sm text-[#4A4A4A]">I want to subscribe to your mailing list.</span>
                </label>
              </div>
              
              {/* reCAPTCHA */}
              <div className="mb-6 flex justify-center">
                {RECAPTCHA_SITE_KEY ? (
                  <ReCAPTCHA
                    ref={recaptchaRef}
                    sitekey={RECAPTCHA_SITE_KEY}
                    onChange={handleRecaptchaChange}
                  />
                ) : (
                  <p className="text-sm text-gray-500">reCAPTCHA loading...</p>
                )}
              </div>
              
              <button
                type="submit"
                disabled={isSubmitting || !recaptchaToken}
                className="w-full bg-[#FF6600] hover:bg-[#FF9933] text-white py-4 rounded-lg font-semibold text-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
              >
                {isSubmitting ? 'Submitting...' : 'Submit'}
              </button>
            </form>
            )}
          </div>
        </div>
      </section>

      {/* Testimonial Section */}
      <section className="py-20 bg-gradient-to-r from-[#FF6600]/5 to-[#FF9933]/10">
        <div className="container mx-auto px-4">
          <div className="text-center mb-3">
            <div className="inline-flex items-center gap-1 mb-3">
              {[...Array(5)].map((_, i) => (
                <svg key={i} className="w-6 h-6 text-[#FF6600] fill-current" viewBox="0 0 20 20">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.955a1 1 0 00.95.69h4.15c.969 0 1.371 1.24.588 1.81l-3.357 2.44a1 1 0 00-.363 1.118l1.287 3.955c.3.921-.755 1.688-1.54 1.118l-3.357-2.44a1 1 0 00-1.175 0l-3.357 2.44c-.784.57-1.838-.197-1.539-1.118l1.287-3.955a1 1 0 00-.363-1.118L2.045 9.382c-.784-.57-.38-1.81.588-1.81h4.15a1 1 0 00.95-.69l1.286-3.955z" />
                </svg>
              ))}
            </div>
            <p className="text-sm text-[#666] font-medium mb-2">Rated 5.0 by Canadian business owners</p>
          </div>
          <h2 className="text-4xl font-bold text-center mb-12 text-[#333333]">What Our Clients Say</h2>
          <div className="max-w-4xl mx-auto">
            <Carousel
              showArrows={true}
              showThumbs={false}
              showStatus={false}
              infiniteLoop={true}
              autoPlay={true}
              interval={6000}
              stopOnHover={true}
              className="testimonial-carousel"
            >
              {[
                {
                  quote: "Our experience with EquipEASE has been outstanding! Their team provided top-notch support from the moment we started our leasing journey. They were responsive to our questions, and their industry expertise was evident throughout the process.",
                  name: 'T. Evans',
                  title: 'Construction Company Owner',
                },
                {
                  quote: "EquipEASE made financing my first truck incredibly easy. As a new owner-operator, I was worried about approval, but they found a solution that worked for my situation. I'm now on the road building my business thanks to their help!",
                  name: 'M. Singh',
                  title: 'Owner-Operator, Transportation',
                },
                {
                  quote: "When we needed to upgrade our dental equipment, EquipEASE offered competitive rates and flexible terms that fit our practice's budget. The whole process was seamless, and we had our new equipment installed within weeks.",
                  name: 'Dr. R. Patel',
                  title: 'Dental Practice Owner',
                },
                {
                  quote: "I've financed three pieces of heavy equipment through EquipEASE over the past two years. Their team truly understands the farming industry and the seasonal nature of our cash flow. Highly recommend them to any Canadian farmer!",
                  name: 'J. MacDonald',
                  title: 'Agricultural Business Owner',
                },
                {
                  quote: "Professional, fast, and reliable. EquipEASE helped us finance our manufacturing equipment when traditional banks turned us down. Their quick approval process kept our expansion project on schedule. A true partner in our growth!",
                  name: 'S. Thompson',
                  title: 'Manufacturing Business Owner',
                },
              ].map((t, idx) => (
                <div key={idx} className="px-8 pb-12">
                  <div className="flex justify-center gap-1 mb-4">
                    {[...Array(5)].map((_, i) => (
                      <svg key={i} className="w-5 h-5 text-[#FF6600] fill-current" viewBox="0 0 20 20">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.955a1 1 0 00.95.69h4.15c.969 0 1.371 1.24.588 1.81l-3.357 2.44a1 1 0 00-.363 1.118l1.287 3.955c.3.921-.755 1.688-1.54 1.118l-3.357-2.44a1 1 0 00-1.175 0l-3.357 2.44c-.784.57-1.838-.197-1.539-1.118l1.287-3.955a1 1 0 00-.363-1.118L2.045 9.382c-.784-.57-.38-1.81.588-1.81h4.15a1 1 0 00.95-.69l1.286-3.955z" />
                      </svg>
                    ))}
                  </div>
                  <div className="text-6xl text-[#FF6600] leading-none mb-2 text-center">"</div>
                  <p className="text-xl text-[#4A4A4A] italic mb-6 text-center leading-relaxed">
                    {t.quote}
                  </p>
                  <p className="font-semibold text-[#333333] text-center">{t.name}</p>
                  <p className="text-sm text-[#666666] text-center">{t.title}</p>
                </div>
              ))}
            </Carousel>
          </div>
        </div>
      </section>

      {/* Industries Section */}
      <section id="industries" className="relative py-20 bg-white overflow-hidden">
        <div className="ee-flow-bg" />
        <div className="container mx-auto px-4 relative z-10">
          <h2 className="text-4xl font-bold text-center mb-4 text-[#333333]">Industries We Work With</h2>
          <div className="w-20 h-1 bg-[#FF6600] mx-auto mb-12"></div>
          
          <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-8 max-w-7xl mx-auto">
            <div className="rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow">
              <img 
                src="https://images.unsplash.com/flagged/photo-1551755537-57a7207a1fb4?w=640&h=400&fit=crop&q=75&fm=webp"
                alt="Operator working with excavator on a sunny construction site - EquipEASE construction equipment financing"
                className="w-full h-64 object-cover"
                width="640" height="400"
                loading="lazy"
                decoding="async"
              />
              <div className="p-6 bg-gray-50">
                <h3 className="text-xl font-semibold text-gray-900">Construction</h3>
              </div>
            </div>
            
            <div className="rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow">
              <img 
                src="https://images.pexels.com/photos/8994766/pexels-photo-8994766.jpeg?auto=compress&cs=tinysrgb&w=640&h=400&fit=crop"
                alt="Semi-truck hauling freight on scenic BC highway with Canadian mountains - EquipEASE transportation financing"
                className="w-full h-64 object-cover"
                width="640" height="400"
                loading="lazy"
                decoding="async"
              />
              <div className="p-6 bg-gray-50">
                <h3 className="text-xl font-semibold text-gray-900">Transportation</h3>
              </div>
            </div>
            
            <div className="rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow">
              <img 
                src="https://images.unsplash.com/photo-1521026483550-6a1a67b2766b?w=640&h=400&fit=crop&q=75&fm=webp"
                alt="Welding sparks flying on factory assembly line - EquipEASE manufacturing equipment financing"
                className="w-full h-64 object-cover"
                width="640" height="400"
                loading="lazy"
                decoding="async"
              />
              <div className="p-6 bg-gray-50">
                <h3 className="text-xl font-semibold text-gray-900">Manufacturing</h3>
              </div>
            </div>

            <div className="rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow">
              <img 
                src="https://images.unsplash.com/photo-1631561379783-8040c0a42df9?w=640&h=400&fit=crop&q=75&fm=webp"
                alt="Dental CAD CAM milling machine in modern laboratory - EquipEASE dental and medical equipment financing"
                className="w-full h-64 object-cover"
                width="640" height="400"
                loading="lazy"
                decoding="async"
              />
              <div className="p-6 bg-gray-50">
                <h3 className="text-xl font-semibold text-gray-900">Dental & Medical</h3>
              </div>
            </div>

            <div className="rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow">
              <img 
                src="https://images.unsplash.com/photo-1594581835488-0b95b8b0bacd?w=640&h=400&fit=crop&q=75&fm=webp"
                alt="Workers at oil and gas drilling facility wearing safety gear - EquipEASE oil and gas equipment financing"
                className="w-full h-64 object-cover"
                width="640" height="400"
                loading="lazy"
                decoding="async"
              />
              <div className="p-6 bg-gray-50">
                <h3 className="text-xl font-semibold text-gray-900">Oil & Gas</h3>
              </div>
            </div>

            <div className="rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow">
              <img 
                src="https://images.unsplash.com/photo-1765109933303-10ec0ab2204a?w=640&h=400&fit=crop&q=75&fm=webp"
                alt="Modern forestry harvester processing timber in a dense Canadian forest - EquipEASE logging equipment financing"
                className="w-full h-64 object-cover"
                width="640" height="400"
                loading="lazy"
                decoding="async"
              />
              <div className="p-6 bg-gray-50">
                <h3 className="text-xl font-semibold text-gray-900">Logging</h3>
              </div>
            </div>

            <div className="rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow">
              <img 
                src="https://images.unsplash.com/photo-1565123870016-4c67f5a41895?w=640&h=400&fit=crop&q=75&fm=webp"
                alt="Farm tractors working golden prairie field under dramatic Canadian sky - EquipEASE agriculture equipment financing"
                className="w-full h-64 object-cover"
                width="640" height="400"
                loading="lazy"
                decoding="async"
              />
              <div className="p-6 bg-gray-50">
                <h3 className="text-xl font-semibold text-gray-900">Agriculture</h3>
              </div>
            </div>

            <div className="rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow">
              <img 
                src="https://images.unsplash.com/photo-1750987776992-19c2abd847ee?w=640&h=400&fit=crop&q=75&fm=webp"
                alt="Landscaper riding commercial mower on green summer lawn - EquipEASE financing for all industries"
                className="w-full h-64 object-cover"
                width="640" height="400"
                loading="lazy"
                decoding="async"
              />
              <div className="p-6 bg-gray-50">
                <h3 className="text-xl font-semibold text-gray-900">All Other Industries</h3>
                <p className="text-sm text-gray-600 mt-2">Landscape & Property Maintenance, Welding, Tree Services, IT, Restaurants & More</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Recently Financed Gallery — Freshness/Social Proof */}
      <section className="relative py-20 bg-gray-50 overflow-hidden">
        <div className="ee-flow-bg" />
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center mb-12 max-w-2xl mx-auto">
            <span className="inline-block bg-[#FF6600]/10 text-[#FF6600] text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full mb-3">
              Recently Financed
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#333333] mb-3">Fresh Approvals Across Canada</h2>
            <p className="text-[#666] text-base md:text-lg">A snapshot of deals we've funded this quarter — from Vancouver Island to Cape Breton.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {[
              { img: 'https://images.unsplash.com/photo-1709735133497-bbead76953a9?w=640&h=480&fit=crop&q=75&fm=webp', amount: '$180K' },
              { img: 'https://images.unsplash.com/photo-1747011950541-4d3eecc36755?w=640&h=480&fit=crop&q=75&fm=webp', amount: '$225K' },
              { img: 'https://images.unsplash.com/photo-1742070122885-1b8aa9726d1a?w=640&h=480&fit=crop&q=75&fm=webp', amount: '$120K' },
              { img: 'https://images.unsplash.com/photo-1714504904786-b6732390b206?w=640&h=480&fit=crop&q=75&fm=webp', amount: '$85K' },
              { img: 'https://images.unsplash.com/photo-1727191800589-82fe78eeafd6?w=640&h=480&fit=crop&q=75&fm=webp', amount: '$95K' },
              { img: 'https://images.unsplash.com/photo-1716915372367-c170ee1e7d24?w=640&h=480&fit=crop&q=75&fm=webp', amount: '$150K' },
            ].map((deal, idx) => (
              <div
                key={idx}
                className="group relative rounded-2xl overflow-hidden bg-white shadow hover:shadow-2xl hover:-translate-y-1 transition-all"
                data-testid={`recent-deal-${idx}`}
              >
                <div className="relative aspect-[4/3] overflow-hidden">
                  <img
                    src={deal.img}
                    alt={`Recently financed equipment - ${deal.amount} funded by EquipEASE`}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                    decoding="async"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
                  <div className="absolute top-3 right-3 bg-[#FF6600] text-white text-xs font-bold uppercase px-3 py-1 rounded-full tracking-wider shadow-lg">
                    Approved
                  </div>
                  <div className="absolute bottom-4 left-4 right-4 text-white">
                    <div className="text-4xl font-extrabold">{deal.amount}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="text-center mt-10">
            <p className="text-sm text-[#666] italic">Deal details anonymized for client privacy. Financing amounts represent actual approved transactions.</p>
          </div>
        </div>
      </section>

      {/* Short-Term Loan Section */}
      <section className="relative py-16 md:py-20 overflow-hidden bg-[#1a1a2e]">
        {/* Animated background pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-72 h-72 bg-[#FF6600] rounded-full blur-[120px]" />
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#FF9933] rounded-full blur-[150px]" />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-5xl mx-auto">
            <div className="grid md:grid-cols-2 gap-10 items-center">

              {/* Left — Copy */}
              <div className="text-left">
                <span className="inline-block bg-[#FF6600]/20 text-[#FF9933] text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full mb-5">
                  Fast Cash for Your Business
                </span>
                <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-white leading-tight mb-4">
                  Need Cash<br />
                  <span className="text-[#FF6600]">Yesterday?</span>
                </h2>
                <p className="text-gray-400 text-lg mb-8 leading-relaxed max-w-md">
                  No collateral. No red tape. Get an unsecured business loan with funds as fast as <span className="text-white font-semibold">48 hours</span>.
                </p>

                <a
                  href="/businessloans"
                  data-testid="short-term-loan-cta"
                  onClick={() => {
                    if (typeof window.gtag === 'function') {
                      window.gtag('event', 'short_term_loan_click');
                    }
                  }}
                  className="group inline-flex items-center bg-[#FF6600] hover:bg-[#FF9933] text-white px-8 py-4 rounded-xl font-bold text-lg transition-all shadow-lg shadow-[#FF6600]/30 hover:shadow-[#FF6600]/50 hover:-translate-y-0.5"
                >
                  Get Funded Now
                  <svg className="w-5 h-5 ml-2 transition-transform group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                  </svg>
                </a>
              </div>

              {/* Right — Feature Cards */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-5 hover:bg-white/10 transition-colors">
                  <div className="w-12 h-12 bg-[#FF6600]/20 rounded-xl flex items-center justify-center mb-3">
                    <svg className="w-6 h-6 text-[#FF6600]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v12m-3-2.818l.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <h3 className="text-white font-bold text-base mb-1">$10K – $500K</h3>
                  <p className="text-gray-400 text-sm">Flexible loan amounts to fit your needs</p>
                </div>

                <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-5 hover:bg-white/10 transition-colors">
                  <div className="w-12 h-12 bg-[#FF6600]/20 rounded-xl flex items-center justify-center mb-3">
                    <svg className="w-6 h-6 text-[#FF6600]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z" />
                    </svg>
                  </div>
                  <h3 className="text-white font-bold text-base mb-1">48-Hour Funding</h3>
                  <p className="text-gray-400 text-sm">From application to cash in your account</p>
                </div>

                <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-5 hover:bg-white/10 transition-colors">
                  <div className="w-12 h-12 bg-[#FF6600]/20 rounded-xl flex items-center justify-center mb-3">
                    <svg className="w-6 h-6 text-[#FF6600]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
                    </svg>
                  </div>
                  <h3 className="text-white font-bold text-base mb-1">No Collateral</h3>
                  <p className="text-gray-400 text-sm">Unsecured — your assets stay untouched</p>
                </div>

                <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-5 hover:bg-white/10 transition-colors">
                  <div className="w-12 h-12 bg-[#FF6600]/20 rounded-xl flex items-center justify-center mb-3">
                    <svg className="w-6 h-6 text-[#FF6600]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
                    </svg>
                  </div>
                  <h3 className="text-white font-bold text-base mb-1">Simple Process</h3>
                  <p className="text-gray-400 text-sm">Quick online application — minimal paperwork</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Have Any Questions Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-[#333] mb-3">Have Any Questions?</h2>
          <p className="text-[#666] text-lg mb-8 max-w-lg mx-auto">We're here to help. Reach out and our team will get back to you quickly.</p>
          <button
            data-testid="questions-contact-btn"
            onClick={() => {
              const el = document.getElementById('contact');
              if (el) el.scrollIntoView({ behavior: 'smooth' });
            }}
            className="inline-flex items-center bg-[#FF6600] hover:bg-[#FF9933] text-white px-8 py-4 rounded-xl font-bold text-lg transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5"
          >
            Contact Us
            <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
            </svg>
          </button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;